import { useState, useRef, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Dialog,
  Tooltip,
} from '@mui/material';
import Fade from '@mui/material/Fade';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import Sidebar from '../../Pages/landingPage/Sidebar';
import {
  Announcement,
  Person4,
  SettingsRounded,
  TrackChanges,
  Business,
  ContactMail,
  RateReview,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AdminTabs } from '../AdminTabs';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { styled } from '@mui/system';
import ListAltRounded from '@mui/icons-material/ListAltRounded';
import EditIcon from '../../Images/Edit.png';
import DeleteIcon from '../../Images/Delete.png';
import Cookies from 'js-cookie';
import {
  createAnnouncement,
  getAnnouncements,
  deleteAnnouncement,
  getAnnouncementById,
  updateAnnouncement,
} from '../../apis/Admin';
import { useTimezone } from '../../hooks/useTimezone';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';

const StyledTextField = styled(TextField)(({ multiline }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    transition: 'all 0.2s ease-in-out',
    height: multiline ? 'auto' : 45,
    alignItems: 'flex-start',
    '& fieldset': {
      border: '0.5px solid #e5e7eb',
    },
    '&:hover fieldset': {
      border: '0.5px solid #d1d5db',
    },
    '&.Mui-focused fieldset': {
      border: '0.6px solid #5965A6',
    },
  },
  '& .MuiInputBase-input': {
    padding: multiline ? '10px 14px' : '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#374151',
  },
  '& .MuiInputLabel-root': {
    color: '#5965A6',
    fontSize: '0.875rem',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#5965A6',
  },
  '& .MuiInputLabel-root.Mui-error': {
    color: '#d32f2f',
  },
}));

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, staggerChildren: 0.1 },
  },
};
const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: (Number(i) || 0) * 0.05,
      duration: 0.3,
      ease: 'easeOut',
    },
  }),
};

// simple word counter (treats sequences of non-space as words)
const countWords = (s) => {
  if (!s || !s.trim().length) return 0;
  const textOnly = s.replace(/<[^>]*>/g, ' ').trim();
  return textOnly ? textOnly.split(/\s+/).filter(Boolean).length : 0;
};

const sanitizeHtml = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
  });
};

export default function AdminAnnoucement() {
  const { formatDate } = useTimezone();
  const [form, setForm] = useState({
    title: '',
    type: '',
    description: '',
    when: null,
    expiry_when: null,
    is_active: true,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // NEW: dialog state (form shown only in dialog)
  const [formOpen, setFormOpen] = useState(false);

  const [listLoading, setListLoading] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const isEditMode = Boolean(editingId);
  const listPaperRef = useRef(null);

  // 🔹 NEW: track which row is being toggled
  const [togglingId, setTogglingId] = useState(null);
  
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  const descriptionWords = useMemo(
    () => countWords(form.description || ''),
    [form.description],
  );

  const getDefaultExpiryDate = (scheduledDate) => {
    if (!scheduledDate) return null;
    const d = new Date(scheduledDate);
    d.setDate(d.getDate() + 7);
    return dayjs(d);
  };

  const rawRole = Cookies.get('Role_id') ?? Cookies.get('role_id');

  const isSuperAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 1;
  }, [rawRole]);

  const scrollToRef = (ref, offset = 16) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = ref?.current;
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Title is required';
    if (!form.description.trim()) e.description = 'Description is required';
    if (descriptionWords > 250)
      e.description = `Maximum 250 words allowed (currently ${descriptionWords})`;
    if (!form.type) e.type = 'Type is required';
    if (!form.when) e.when = 'Date & Time is required';
    if (!form.expiry_when) {
      e.expiry_when = 'Expiry date is required';
    } else if (form.when && form.expiry_when && form.expiry_when.isBefore(form.when)) {
      e.expiry_when = 'Expiry date must be after scheduled date & time';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleDeleteAnnouncement = async (announcement_id) => {
    const prev = announcements; // snapshot for rollback
    // optimistic remove
    setAnnouncements((cur) =>
      cur.filter((a) => a.announcement_id !== announcement_id),
    );
    setDeletingId(announcement_id);

    try {
      const res = await deleteAnnouncement(announcement_id);
      toast.success(res?.message || 'Announcement deleted', {
        autoClose: 2000,
      });
    } catch (err) {
      // rollback
      setAnnouncements(prev);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to delete announcement',
      );
    } finally {
      setDeletingId(null);
    }
  };

  const set = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e && e.target ? e.target.value : e }));

  // fetch all announcements (optionally skip scroll)
  const handleAllAnnouncementClick = async ({ skipScroll = false } = {}) => {
    setListLoading(true);
    try {
      const res = await getAnnouncements();

      // normalize API shape → array
      const items = Array.isArray(res)
        ? res
        : Array.isArray(res?.announcements)
          ? res.announcements
          : [];

      // optional: newest first
      items.sort(
        (a, b) =>
          new Date(b.scheduled_datetime) - new Date(a.scheduled_datetime),
      );

      setAnnouncements(items);

      if (!skipScroll) {
        scrollToRef(listPaperRef, 16);
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || 'Failed to fetch announcements',
      );
      setAnnouncements([]);
    } finally {
      setListLoading(false);
    }
  };

  // 🔹 Load table by default when tab is opened
  useEffect(() => {
    handleAllAnnouncementClick({ skipScroll: true });
  }, []);

  const handleToggleStatus = async (announcement) => {
    const { announcement_id } = announcement;
    const nextStatus = !announcement.is_active;

    // 🔹 Optimistic UI update
    const prev = announcements;
    setAnnouncements((cur) =>
      cur.map((a) =>
        a.announcement_id === announcement_id
          ? { ...a, is_active: nextStatus }
          : a,
      ),
    );
    setTogglingId(announcement_id);

    try {
      let expiryDateToUse = announcement.expiry_date;
      if (nextStatus) {
        const currentDate = new Date();
        const expiryDate = announcement.expiry_date 
          ? new Date(announcement.expiry_date) 
          : null;
        
        if (!expiryDate || expiryDate < currentDate) {
          const newExpiryDate = new Date();
          newExpiryDate.setDate(newExpiryDate.getDate() + 7);
          expiryDateToUse = newExpiryDate.toISOString();
        }
      }

      await updateAnnouncement(announcement_id, {
        title: announcement.title,
        announcement_type: announcement.announcement_type,
        description: announcement.description,
        scheduled_datetime: announcement.scheduled_datetime,
        expiry_date: expiryDateToUse,
        is_active: nextStatus,
      });

      toast.success(
        `Announcement marked as ${nextStatus ? 'Active' : 'Inactive'}.`,
        { autoClose: 1500 },
      );

      await handleAllAnnouncementClick({ skipScroll: true });
    } catch (err) {
      // rollback on error
      setAnnouncements(prev);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to update status',
      );
    } finally {
      setTogglingId(null);
    }
  };

  const onPublish = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const scheduledISO = form.when ? form.when.toDate().toISOString() : null;
      const expiryISO = form.expiry_when ? form.expiry_when.toDate().toISOString() : null;

      const sanitizedDescription = sanitizeHtml(form.description);

      const payload = {
        title: form.title.trim(),
        announcement_type: form.type, // match API key
        description: sanitizedDescription,
        scheduled_datetime: scheduledISO,
        expiry_date: expiryISO,
      };

      await createAnnouncement(payload);

      toast.success('Announcement published successfully.');
      await handleAllAnnouncementClick();
      setForm({ title: '', type: '', description: '', when: null, expiry_when: null, is_active: true });
      setErrors({});
      setFormOpen(false); // 🔹 close dialog after publish
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to publish announcement';
      setErrors((s) => ({ ...s, publish: msg }));
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    setErrors({});
    setEditingId(null);
    setForm({ title: '', type: '', description: '', when: null, expiry_when: null, is_active: true });
    setFormOpen(false); // 🔹 close dialog
  };

  const handleViewClick = async (announcement_id) => {
    try {
      const a = await getAnnouncementById(announcement_id);
      setViewData(a);
      setViewOpen(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || 'Failed to load announcement',
      );
    }
  };

  const handleEditClick = async (announcement_id) => {
    try {
      const a = await getAnnouncementById(announcement_id);
      
      setForm({
        title: a?.title ?? '',
        type: a?.announcement_type ?? '',
        description: a?.description ?? '',
        when: a?.scheduled_datetime ? dayjs(a.scheduled_datetime) : null,
        expiry_when: a?.expiry_date ? dayjs(a.expiry_date) : null,
        is_active: a?.is_active ?? true,
      });
      setErrors({});
      setEditingId(announcement_id);
      setFormOpen(true); // 🔹 open dialog in edit mode
      toast.info('Loaded announcement for editing', { autoClose: 1200 });
    } catch (err) {
      toast.error(
        err?.response?.data?.message || 'Failed to load announcement',
      );
    }
  };

  const onUpdate = async () => {
    if (!isEditMode || !editingId) return;
    if (!validate()) return;
    setLoading(true);
    try {
      const scheduledISO = form.when ? form.when.toDate().toISOString() : null;
      const expiryISO = form.expiry_when ? form.expiry_when.toDate().toISOString() : null;

      const sanitizedDescription = sanitizeHtml(form.description);

      const payload = {
        title: form.title.trim(),
        announcement_type: form.type,
        description: sanitizedDescription,
        scheduled_datetime: scheduledISO,
        expiry_date: expiryISO,
        is_active: form.is_active,
      };

      await updateAnnouncement(editingId, payload);

      toast.success('Announcement updated successfully.');
      await handleAllAnnouncementClick();
      setEditingId(null);
      setForm({ title: '', type: '', description: '', when: null, expiry_when: null, is_active: true });
      setErrors({});
      setFormOpen(false); // 🔹 close dialog after update
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to update announcement',
      );
    } finally {
      setLoading(false);
    }
  };

  const thSx = {
    fontSize: '0.75rem',
    fontWeight: 500,
    py: 1,
    px: 2,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E5E7EB',
  };
  const tdIndexSx = {
    py: 0.25,
    px: 2,
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#6b7280',
    verticalAlign: 'middle',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        bgcolor: '#f8fafc',
        minHeight: '100vh',
      }}
    >
      <Sidebar />
      <Box sx={{ flexGrow: 1, px: 3, py: 2 }}>
        <AdminTabs
          items={[
              ...(isSuperAdmin
                ? [
                    {
                      to: '/admin/organizations',
                      label: 'Organizations',
                      icon: Business,
                    },
                  ]
                : []),
            { to: '/admin/user', label: 'User Management', icon: Person4 },
            {
              to: '/admin/annoucement',
              label: 'Annoucement',
              icon: Announcement,
            },
            {
              to: '/admin/setting',
              label: 'Application Settings',
              icon: SettingsRounded,
            },
            {
              to: '/admin/audit-log',
              label: 'Audit Logs',
              icon: TrackChanges,
            },
            ...(isSuperAdmin
              ? [
                  {
                    to: '/admin/contact-submissions',
                    label: 'Contact Submissions',
                    icon: ContactMail,
                  },
                  {
                    to: '/admin/public-reviews',
                    label: 'Public Reviews',
                    icon: RateReview,
                  }
                ]
              : []),
          ]}
        />

        <AnimatePresence>
          {/* Header strip only (no inline form) */}
          <Paper
            component={motion.div}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              background: '#fff',
              border: '1px solid #e2e8f0',
              mt: 1,
            }}
          >
            <Box
              component={motion.div}
              variants={rowVariants}
              custom={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                mb: 0,
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  component="h2"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 600,
                    color: '#111827',
                    letterSpacing: '0.03em',
                    lineHeight: 1.2,
                  }}
                >
                  Announcement
                </Typography>
                <Typography sx={{ fontSize: '0.825rem', color: '#4b505aff' }}>
                  All announcements will remain{' '}
                  <span style={{ fontWeight: 600 }}>Active </span> until the
                  expiry date & time you specify.
                </Typography>
              </Box>

              {/* 🔹 This button now opens the dialog with the form */}
              <Button
                variant="contained"
                onClick={() => {
                  setEditingId(null);
                  setForm({
                    title: '',
                    type: '',
                    description: '',
                    when: null,
                    expiry_when: null,
                    is_active: true,
                  });
                  setErrors({});
                  setFormOpen(true);
                }}
                startIcon={<ListAltRounded sx={{ fontSize: 20 }} />}
                sx={{
                  mr: 1.5,
                  fontSize: '0.875rem',
                  backgroundColor: '#5965A6',
                  padding: '6px 12px',
                  color: 'white',
                  textTransform: 'none',
                  textWrap: 'nowrap',
                  borderRadius: '6px',
                  fontWeight: 500,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                  transition:
                    'transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 200ms ease',
                  '&:hover': {
                    backgroundColor: '#4C5B9A',
                    transform: 'translateY(-1px) scale(1.02)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
                  },
                }}
              >
                Add Announcement
              </Button>
            </Box>
          </Paper>

          {/* 🔹 Table is always visible */}
          <Paper
            ref={listPaperRef}
            component={motion.div}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            sx={{
              mt: 2,
              overflow: 'hidden',
              border: '1px solid #e2e8f0',
              mb: '2rem',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  sx={{
                    color: '#5965A6',
                    fontWeight: 'bold',
                    letterSpacing: 1,
                  }}
                >
                  All Announcements
                </Typography>
              </Box>

              <AnimatePresence initial={false}>
                <Box
                  component={motion.div}
                  key="announcements-table"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.28, ease: 'easeOut' }}
                  sx={{ overflow: 'hidden', px: 2, py: 2 }}
                >
                  {listLoading && (
                    <Typography
                      sx={{ color: '#64748b', fontSize: '0.9rem', mb: 1 }}
                    >
                      Loading announcements…
                    </Typography>
                  )}

                  <TableContainer sx={{ overflow: 'hidden', borderRadius: 1 }}>
                    <Table
                      size="small"
                      sx={{
                        width: '100%',
                        '& .MuiTableCell-root': {
                          py: 1,
                          px: 1,
                          borderBottom: '1px solid #e5e7eb',
                        },
                        '& thead .MuiTableCell-root': { py: 0.75, px: 1.25 },
                        '& .MuiTableRow-root': { height: 44 },
                      }}
                    >
                      <TableHead sx={{ backgroundColor: '#5965A6' }}>
                        <TableRow
                          sx={{
                            '&:hover': { backgroundColor: '#4C5B9A' },
                            transition: 'background-color 0.2s ease',
                          }}
                        >
                          <TableCell sx={thSx}>Sr No</TableCell>
                          <TableCell sx={thSx}>Title</TableCell>
                          <TableCell sx={thSx}>Type</TableCell>
                          {isSuperAdmin && (
                            <TableCell sx={thSx}>Organization</TableCell>
                          )}
                          <TableCell sx={thSx}>Scheduled</TableCell>
                          <TableCell sx={thSx}>Status</TableCell>
                          <TableCell sx={thSx}>Actions</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {!announcements || announcements.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={isSuperAdmin ? 7 : 6}
                              align="center"
                              sx={{ py: 4 }}
                            >
                              <Typography
                                sx={{
                                  color: '#64748b',
                                  fontSize: '0.95rem',
                                }}
                              >
                                No announcements yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          announcements.map((a, i) => {
                            // Compute status once per announcement
                            const isExpired = a.expiry_date 
                              ? new Date(a.expiry_date) < new Date()
                              : false;
                            const isActuallyActive = a.is_active && !isExpired;
                            
                            return (
                            <TableRow
                              key={a.announcement_id || i}
                              sx={{ '&:hover': { background: '#F8FAFF' } }}
                            >
                              <TableCell sx={tdIndexSx}>{i + 1}</TableCell>
                              <TableCell
                                sx={{ py: 0.25, px: 2, fontSize: '0.875rem' }}
                              >
                                {a.title}
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 0.25,
                                  px: 2,
                                  fontSize: '0.875rem',
                                  color: '#6b7280',
                                  textTransform: 'capitalize',
                                }}
                              >
                                {a.announcement_type?.replace('_', ' ') || '-'}
                              </TableCell>
                              {isSuperAdmin && (
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    color: '#6b7280',
                                  }}
                                >
                                  {a.org_name}
                                </TableCell>
                              )}
                              <TableCell
                                sx={{
                                  py: 0.25,
                                  px: 2,
                                  fontSize: '0.875rem',
                                  color: '#6b7280',
                                }}
                              >
                                {a.scheduled_datetime
                                  ? formatDate(a.scheduled_datetime, {
                                      includeTime: true,
                                    })
                                  : '-'}
                              </TableCell>
                              <TableCell sx={{ py: 0.25, px: 2 }}>
                                <Tooltip
                                  title={
                                    togglingId === a.announcement_id
                                      ? 'Updating status…'
                                      : isActuallyActive
                                        ? 'Click to mark as Inactive'
                                        : 'Click to mark as Active'
                                  }
                                  arrow
                                >
                                  <Box
                                    onClick={() => {
                                      if (togglingId) return; // prevent multiple rapid clicks
                                      handleToggleStatus(a);
                                    }}
                                    sx={{
                                      display: 'inline-block',
                                      px: 1.25,
                                      py: 0.35,
                                      borderRadius: '10px',
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      letterSpacing: '0.02em',
                                      cursor:
                                        togglingId === a.announcement_id
                                          ? 'not-allowed'
                                          : 'pointer',
                                      opacity:
                                        togglingId === a.announcement_id
                                          ? 0.6
                                          : 1,
                                      color: isActuallyActive
                                        ? '#2e7d32'
                                        : '#ed6c02',
                                      bgcolor: isActuallyActive
                                        ? 'rgba(46,125,50,0.12)'
                                        : 'rgba(237,108,2,0.12)',
                                      textTransform: 'capitalize',
                                      transition:
                                        'background-color 0.2s ease, transform 0.15s ease',
                                      '&:hover': {
                                        transform:
                                          togglingId === a.announcement_id
                                            ? 'none'
                                            : 'translateY(-1px)',
                                        backgroundColor: isActuallyActive
                                          ? 'rgba(46,125,50,0.18)'
                                          : 'rgba(237,108,2,0.18)',
                                      },
                                    }}
                                  >
                                    {togglingId === a.announcement_id
                                      ? 'Updating…'
                                      : isActuallyActive ? 'Active' : 'Inactive'}
                                  </Box>
                                </Tooltip>
                              </TableCell>

                              <TableCell sx={{ py: 0.25, px: 2 }}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Tooltip title="View Details" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewClick(a.announcement_id);
                                      }}
                                    >
                                      <VisibilityRounded sx={{ fontSize: 20, color: '#5965A6' }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete" arrow>
                                    <IconButton
                                      size="small"
                                      disabled={deletingId === a.announcement_id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteAnnouncement(
                                          a.announcement_id,
                                        );
                                      }}
                                    >
                                      <img
                                        src={DeleteIcon}
                                        alt="Delete"
                                        style={{ width: 22, height: 22 }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditClick(a.announcement_id);
                                      }}
                                    >
                                      <img
                                        src={EditIcon}
                                        alt="Edit"
                                        style={{ width: 22, height: 22 }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                            })
                          )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </AnimatePresence>
            </Box>
          </Paper>
        </AnimatePresence>

        {/* 🔹 Dialog with the SAME form (fields, styling, buttons) */}
        <Dialog
          open={formOpen}
          onClose={onCancel}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
                color: '#111827',
                mb: 2,
              }}
            >
              {isEditMode ? 'Edit Announcement' : 'New Announcement'}
            </Typography>

            <Grid container spacing={2}>
              {/* Title */}
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Title"
                  placeholder="Enter Announcement Title"
                  value={form.title}
                  onChange={set('title')}
                  error={!!errors.title}
                  helperText={errors.title || ' '}
                />
              </Grid>

              {/* Type */}
              <Grid item xs={12} md={6}>
                <StyledTextField
                  select
                  fullWidth
                  label="Type"
                  value={form.type}
                  onChange={set('type')}
                  error={!!errors.type}
                  helperText={errors.type || ' '}
                  SelectProps={{
                    IconComponent: KeyboardArrowDownRounded,
                    MenuProps: {
                      TransitionComponent: Fade,
                    },
                  }}
                >
                  <MenuItem value="new_feature">New feature</MenuItem>
                  <MenuItem value="announcement">Announcement</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                </StyledTextField>
              </Grid>

              {/* Date & Time */}
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Date & Time"
                    value={form.when}
                    disablePast={!isEditMode}
                    minDateTime={!isEditMode ? dayjs() : undefined}
                    onChange={(v) => {
                      const now = dayjs();
                      if (!v || !v.isValid()) {
                        setErrors((s) => ({
                          ...s,
                          when: 'Please choose a valid date & time',
                        }));
                        setForm((s) => ({ ...s, when: null }));
                        return;
                      }
                      if (v.isBefore(now)) {
                        setErrors((s) => ({
                          ...s,
                          when: 'Past date & time not allowed',
                        }));
                        return;
                      }
                      setErrors((s) => ({ ...s, when: undefined }));
                      setForm((s) => ({ ...s, when: v }));
                      if (!form.expiry_when) {
                        const defaultExpiry = getDefaultExpiryDate(v.toDate().toISOString());
                        if (defaultExpiry) {
                          setForm((s) => ({ ...s, expiry_when: defaultExpiry }));
                        }
                      } else if (form.expiry_when.isBefore(v)) {
                        const defaultExpiry = getDefaultExpiryDate(v.toDate().toISOString());
                        if (defaultExpiry) {
                          setForm((s) => ({ ...s, expiry_when: defaultExpiry }));
                        }
                      }
                    }}
                    onError={(reason) => {
                      setErrors((s) => ({
                        ...s,
                        when: reason
                          ? 'Past date & time not allowed'
                          : undefined,
                      }));
                    }}
                    slots={{ textField: StyledTextField }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: 'Select date and time',
                        error: !!errors.when,
                        helperText: errors.when || ' ',
                      },
                      openPickerButton: {
                        sx: {
                          width: 36,
                          height: 36,
                          m: 0.25,
                          '& .MuiSvgIcon-root': {
                            fontSize: 20,
                          },
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: 45,
                        alignItems: 'center',
                      },
                      '& .MuiInputAdornment-root': {
                        m: 0,
                      },
                      '& .MuiInputAdornment-root .MuiIconButton-root': {
                        width: 36,
                        height: 36,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Expiry Date & Time"
                    value={form.expiry_when}
                    disablePast
                    minDateTime={form.when || dayjs()}
                    onChange={(v) => {
                      if (!v || !v.isValid()) {
                        setErrors((s) => ({
                          ...s,
                          expiry_when: 'Please choose a valid expiry date & time',
                        }));
                        setForm((s) => ({ ...s, expiry_when: null }));
                        return;
                      }
                      if (form.when && v.isBefore(form.when)) {
                        setErrors((s) => ({
                          ...s,
                          expiry_when: 'Expiry date must be after scheduled date & time',
                        }));
                        return;
                      }
                      setErrors((s) => ({ ...s, expiry_when: undefined }));
                      setForm((s) => ({ ...s, expiry_when: v }));
                    }}
                    onError={(reason) => {
                      setErrors((s) => ({
                        ...s,
                        expiry_when: reason
                          ? 'Please choose a valid expiry date & time'
                          : undefined,
                      }));
                    }}
                    slots={{ textField: StyledTextField }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: 'Select expiry date and time',
                        error: !!errors.expiry_when,
                        helperText: errors.expiry_when || ' ',
                      },
                      openPickerButton: {
                        sx: {
                          width: 36,
                          height: 36,
                          m: 0.25,
                          '& .MuiSvgIcon-root': {
                            fontSize: 20,
                          },
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: 45,
                        alignItems: 'center',
                      },
                      '& .MuiInputAdornment-root': {
                        m: 0,
                      },
                      '& .MuiInputAdornment-root .MuiIconButton-root': {
                        width: 36,
                        height: 36,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {isEditMode && (
                <Grid
                  item
                  xs={12}
                  md={6}
                  component={motion.div}
                  custom={2.5}
                  variants={rowVariants}
                >
                  <StyledTextField
                    select
                    fullWidth
                    label="Status"
                    value={form.is_active ? 'active' : 'inactive'}
                    onChange={(e) => {
                      setForm((s) => ({ ...s, is_active: e.target.value === 'active' }));
                    }}
                    SelectProps={{
                      IconComponent: KeyboardArrowDownRounded,
                      MenuProps: {
                        TransitionComponent: Fade,
                      },
                    }}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </StyledTextField>
                </Grid>
              )}

              {/* Description */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: errors.description ? '#d32f2f' : '#5965A6',
                      mb: 0.5,
                      fontWeight: 400,
                    }}
                  >
                    Description
                  </Typography>
                  <Box
                    sx={{
                      '& .quill': {
                        border: errors.description
                          ? '1px solid #d32f2f'
                          : '0.5px solid #e5e7eb',
                        borderRadius: '4px',
                        '&:hover': {
                          border: errors.description
                            ? '1px solid #d32f2f'
                            : '0.5px solid #d1d5db',
                        },
                        '& .ql-toolbar': {
                          borderTopLeftRadius: '4px',
                          borderTopRightRadius: '4px',
                          borderBottom: '0.5px solid #e5e7eb',
                        },
                        '& .ql-container': {
                          borderBottomLeftRadius: '4px',
                          borderBottomRightRadius: '4px',
                          fontSize: '0.9rem',
                          minHeight: '120px',
                        },
                        '& .ql-editor': {
                          minHeight: '120px',
                          '& p, & ol, & ul': {
                            margin: '0.5em 0',
                          },
                        },
                        '&.ql-focused': {
                          border: '0.6px solid #5965A6',
                        },
                      },
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={form.description}
                      onChange={(value) => setForm((s) => ({ ...s, description: value }))}
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, false] }],
                          ['bold', 'italic', 'underline'],
                          [{ list: 'ordered' }, { list: 'bullet' }],
                          ['link'],
                          ['clean'],
                        ],
                      }}
                      formats={[
                        'header',
                        'bold',
                        'italic',
                        'underline',
                        'list',
                        'bullet',
                        'link',
                      ]}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: errors.description ? '#d32f2f' : '#6b7280',
                      mt: 0.5,
                      ml: 1.5,
                    }}
                  >
                    {errors.description || `${descriptionWords}/250 words` || ' '}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Buttons */}
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1.5,
              }}
            >
              <Button
                variant="contained"
                size="small"
                sx={{
                  bgcolor: '#EEF2FF',
                  color: '#5965A6',
                  border: '1px solid #E5E7EB',
                }}
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                sx={{ bgcolor: '#5965A6' }}
                onClick={isEditMode ? onUpdate : onPublish}
                disabled={loading}
              >
                {loading
                  ? isEditMode
                    ? 'Updating…'
                    : 'Publishing…'
                  : isEditMode
                    ? 'Update'
                    : 'Publish'}
              </Button>
            </Box>
          </Box>
        </Dialog>

        <Dialog
          open={viewOpen}
          onClose={() => {
            setViewOpen(false);
            setViewData(null);
          }}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
                color: '#111827',
                mb: 3,
              }}
            >
              Announcement Details
            </Typography>

            {viewData && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Title
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 500 }}>
                    {viewData.title || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Type
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 500, textTransform: 'capitalize' }}>
                    {viewData.announcement_type?.replace('_', ' ') || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Scheduled Date & Time
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 500 }}>
                    {viewData.scheduled_datetime
                      ? formatDate(viewData.scheduled_datetime, { includeTime: true })
                      : '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Expiry Date & Time
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 500 }}>
                    {viewData.expiry_date
                      ? formatDate(viewData.expiry_date, { includeTime: true })
                      : '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Status
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 1.25,
                      py: 0.35,
                      borderRadius: '10px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: viewData.is_active ? '#2e7d32' : '#ed6c02',
                      bgcolor: viewData.is_active
                        ? 'rgba(46,125,50,0.12)'
                        : 'rgba(237,108,2,0.12)',
                      textTransform: 'capitalize',
                    }}
                  >
                    {viewData.is_active ? 'Active' : 'Inactive'}
                  </Box>
                </Grid>

                {isSuperAdmin && viewData.org_name && (
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                      Organization
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 500 }}>
                      {viewData.org_name || '-'}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Description
                  </Typography>
                  <Box
                    sx={{
                      fontSize: '0.9rem',
                      color: '#111827',
                      wordBreak: 'break-word',
                      '& a': {
                        color: '#5965A6',
                        textDecoration: 'underline',
                        '&:hover': {
                          color: '#4C5B9A',
                        },
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(viewData.description || '-'),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Created At
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 500 }}>
                    {viewData.created_at
                      ? formatDate(viewData.created_at, { includeTime: true })
                      : '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Last Updated
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 500 }}>
                    {viewData.updated_at
                      ? formatDate(viewData.updated_at, { includeTime: true })
                      : '-'}
                  </Typography>
                </Grid>
              </Grid>
            )}

            <Box
              sx={{
                mt: 3,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="contained"
                size="small"
                sx={{ bgcolor: '#5965A6' }}
                onClick={() => {
                  setViewOpen(false);
                  setViewData(null);
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Dialog>
      </Box>
    </Box>
  );
}
